// Navbar.jsx - With User Authentication Support
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../CartContext';
import { departments, categories as dataCategories } from '../data';
import { Icons } from './Icons';
import { getAllProducts, getB2BCategories } from '../api';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

export default function Navbar({ onOpenSignup, onOpenLogin, onOpenLocation }) {
  const { cartCount, setIsCartOpen, clearCart, logoutCart, userType, switchUserType } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [logoutPopup, setLogoutPopup] = useState(false);
  const [dbProducts, setDbProducts] = useState([]);
  const [b2bNavCategories, setB2bNavCategories] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch B2C products + B2B categories (and their products) for search
  useEffect(() => {
    let active = true;

    // B2C products
    getAllProducts()
      .then(res => {
        if (!active) return;
        if (res.success && res.products?.length)
          setDbProducts(prev => {
            const existing = new Set(prev.filter(p => p._type === 'b2c').map(p => p.id));
            const newOnes = res.products.filter(p => !existing.has(p.id)).map(p => ({ ...p, _type: 'b2c' }));
            return newOnes.length ? [...prev, ...newOnes] : prev;
          });
      })
      .catch(() => {});

    // B2B categories + products
    getB2BCategories()
      .then(async res => {
        if (!active || !res.success) return;
        setB2bNavCategories(res.categories || []);
        const { getB2BProductsByCategory } = await import('../api');
        const results = await Promise.allSettled(
          (res.categories || []).map(cat => getB2BProductsByCategory(cat.id))
        );
        if (!active) return;
        const b2bProds = results.flatMap(r =>
          r.status === 'fulfilled' && r.value.success
            ? r.value.products.map(p => ({ ...p, _type: 'b2b' }))
            : []
        );
        if (b2bProds.length) {
          setDbProducts(prev => {
            const existing = new Set(prev.filter(p => p._type === 'b2b').map(p => p.id));
            const newOnes = b2bProds.filter(p => !existing.has(p.id));
            return newOnes.length ? [...prev, ...newOnes] : prev;
          });
        }
      })
      .catch(() => {});

    return () => { active = false; };
  }, []);

  // Check if user is logged in on component mount and when localStorage changes
  useEffect(() => {
    const checkUser = () => {
      const userData = localStorage.getItem('currentUser');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };
    
    checkUser();
    
    // Listen for storage changes (in case user logs in/out in another tab)
    window.addEventListener('storage', checkUser);
    // Listen for same-tab auth updates after login/logout.
    window.addEventListener('auth-changed', checkUser);
    // Refresh when tab becomes active again.
    window.addEventListener('focus', checkUser);
    document.addEventListener('visibilitychange', checkUser);
    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('auth-changed', checkUser);
      window.removeEventListener('focus', checkUser);
      document.removeEventListener('visibilitychange', checkUser);
    };
  }, []);

  // Live product suggestions — deduplicated by id+type, name match only
  const suggestions = searchVal.trim().length >= 1
    ? (() => {
        const seen = new Set();
        return dbProducts.filter(p => {
          const key = `${p._type}-${p.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return p.name.toLowerCase().includes(searchVal.toLowerCase());
        }).slice(0, 6);
      })()
    : [];
  
  const [currentLocation, setCurrentLocation] = useState(null);

  // Load location from localStorage
  useEffect(() => {
    const checkLocation = () => {
      const locData = localStorage.getItem('userLocation');
      if (locData) {
        try {
          setCurrentLocation(JSON.parse(locData));
        } catch (e) {
          setCurrentLocation(null);
        }
      } else {
        setCurrentLocation(null);
      }
    };
    
    checkLocation();
    window.addEventListener('storage', checkLocation);
    // Custom event for same-tab updates
    window.addEventListener('location-updated', checkLocation);
    return () => {
      window.removeEventListener('storage', checkLocation);
      window.removeEventListener('location-updated', checkLocation);
    };
  }, []);

  // Handle bottom nav click for B2B categories
  const handleCategoryClick = (categoryName) => {
    navigate(`/b2b/products/${encodeURIComponent(categoryName)}`);
  };

  // Handle home click
  const handleHomeClick = () => {
    if (userType === 'b2b') navigate('/b2b');
    else navigate('/');
  };

  // B2B nav links (used in mobile menu) — uses DB categories
  const navLinks = [];

  const handleUserTypeChange = (type) => {
    switchUserType(type);
    if (type === 'b2c') navigate('/');
    else navigate('/b2b');
  };

  // Route wins for toggle highlight so /b2b never shows B2C selected
  const mode =
    location.pathname === '/b2b' || location.pathname.startsWith('/b2b/')
      ? 'b2b'
      : location.pathname === '/' || location.pathname.startsWith('/category/')
        ? 'b2c'
        : userType;

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    logoutCart();
    switchUserType('b2c');
    window.dispatchEvent(new Event('auth-changed'));
    setUser(null);
    setShowUserMenu(false);
    setLogoutPopup(true);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchVal.trim();
    if (!q) return;
    setSearchVal('');
    setShowSuggestions(false);
    navigate(`/search/${encodeURIComponent(q)}`);
  };

  // Handle clicking a suggestion — route to correct page based on type
  const handleSuggestionClick = (product) => {
    if (product._type === 'b2b') {
      navigate(`/b2b/products/${encodeURIComponent(product.category)}/${product.category_id}`);
    } else {
      navigate(`/category/${encodeURIComponent(product.category)}/${product.category_id}`);
    }
    setSearchVal('');
    setShowSuggestions(false);
  };

  // Get display name for user
  const getDisplayName = () => {
    if (!user) return '';
    return user.full_name || user.name || user.email?.split('@')[0] || 'User';
  };

  const getFirstLetter = () => getDisplayName().charAt(0).toUpperCase();

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .navbar-top {
            padding: 6px 0 !important;
          }
          .navbar-top-content {
            flex-direction: column !important;
            gap: 8px !important;
            align-items: flex-start !important;
          }
          .navbar-contact {
            font-size: 0.7rem !important;
            gap: 16px !important;
          }
          .navbar-auth {
            width: 100% !important;
            justify-content: center !important;
          }
          .navbar-main {
            padding: 8px 0 !important;
          }
          .navbar-search {
            max-width: none !important;
            flex: 1 !important;
          }
          .navbar-search input {
            font-size: 16px !important; /* Prevents zoom on iOS */
          }
          .navbar-actions {
            gap: 4px !important;
          }
          .navbar-btn {
            padding: 0 8px !important;
            font-size: 0.75rem !important;
            height: 36px !important;
          }
          .navbar-toggle {
            display: flex !important;
          }
          .navbar-desktop-search {
            display: none !important;
          }
          .navbar-category-pills {
            gap: 4px !important;
            padding: 6px 0 !important;
          }
          .navbar-category-pill {
            font-size: 0.75rem !important;
            padding: 4px 10px !important;
          }
        }
        @media (max-width: 480px) {
          .navbar-contact {
            flex-wrap: wrap !important;
            gap: 12px !important;
          }
          .navbar-contact span {
            font-size: 0.65rem !important;
          }
          .navbar-actions {
            gap: 2px !important;
          }
          .navbar-btn {
            padding: 0 6px !important;
            font-size: 0.7rem !important;
            min-width: 32px !important;
          }
          .mobile-menu {
            width: 280px !important;
          }
        }
      `}</style>
      {/* Logout success popup */}
      {logoutPopup && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '16px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px',
            maxWidth: '360px', width: '92%',
            padding: '2rem 1.75rem',
            textAlign: 'center',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            animation: 'fadeInUp 0.3s ease'
          }}>
            <style>{`
              @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(16px); }
                to   { opacity: 1; transform: translateY(0); }
              }
            `}</style>

            {/* Icon */}
            <div style={{
              width: '52px', height: '52px',
              background: '#fef3c7', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </div>

            <h2 style={{ margin: '0 0 0.3rem', fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>
              Signed Out
            </h2>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.85rem', color: '#6b7280' }}>
              You've been logged out successfully. See you soon!
            </p>

            <button
              onClick={() => { setLogoutPopup(false); navigate('/'); }}
              style={{
                width: '100%',
                padding: '0.65rem 1rem',
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                color: 'white', border: 'none', borderRadius: '8px',
                fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(22,163,74,0.25)',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Back to Home
            </button>
          </div>
        </div>
      )}

      {/* Top bar - Redesigned */}
      <div style={{ background: '#16a34a', padding: '8px 0', color: 'white' }} className="navbar-top">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }} className="navbar-top-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', fontSize: '0.75rem' }} className="navbar-contact">
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                <span>Mon-Sat: 7AM - 10PM</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                <span>+91 98765 43210</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <span>kilomart685@gmail.com</span>
              </span>
            </div>
            
            {/* Show Sign In/Register OR User info based on login status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.75rem' }} className="navbar-auth">
              {!user ? (
                // Show Sign In and Register buttons when not logged in
                <>
                  <button onClick={onOpenLogin} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: 'white', fontSize: '0.75rem', fontWeight: 500, padding: '4px 12px', borderRadius: '20px', transition: 'background 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
                    Sign In
                  </button>
                  <button onClick={onOpenSignup} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: 'white', fontSize: '0.75rem', fontWeight: 500, padding: '4px 12px', borderRadius: '20px', transition: 'background 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
                    Register
                  </button>
                </>
              ) : (
                // Show user info when logged in
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.75rem', color: 'white' }}>
                    Welcome, {getDisplayName()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <div style={{ padding: '10px 0', borderBottom: '1px solid #e5e7eb', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} className="navbar-main">
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

            {/* Logo */}
          <a
  href={mode === 'b2c' ? '/' : '/b2b'}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textDecoration: 'none',
    flexShrink: 0,
    marginRight: '4px'
  }}
>
  <img
    src="/favicon.svg"
    alt="ePayhub Store"
    width={28}
    height={28}
    style={{ display: 'block', borderRadius: 6 }}
  />

  <span
    style={{
      fontWeight: 800,
      fontSize: '1.25rem',
      color: '#16a34a',
      letterSpacing: '-0.5px'
    }}
  >
    ePayhub <span style={{ color: '#1f2937' }}>Store</span>
  </span>
</a>


            {/* Search */}
            <div style={{ flex: 1, maxWidth: 460, position: 'relative' }} className="navbar-search navbar-desktop-search">
              <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', borderRadius: '8px', border: '1.5px solid transparent', transition: 'border-color 0.2s' }}
                onFocusCapture={e => e.currentTarget.style.borderColor = '#16a34a'}
                onBlurCapture={e => e.currentTarget.style.borderColor = 'transparent'}
              >
                <input
                  type="search"
                  placeholder={mode === 'b2b' ? 'Search wholesale products...' : 'Search groceries, vegetables, fruits...'}
                  value={searchVal}
                  onChange={e => { setSearchVal(e.target.value); setShowSuggestions(true); }}
                  style={{ flex: 1, padding: '0 12px', height: '25px', background: 'transparent', border: 'none', fontSize: '0.875rem', outline: 'none', color: '#1f2937' }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                />
                <button type="submit" style={{ padding: '0 14px', height: '36px', background: '#16a34a', border: 'none', borderRadius: '0 6px 6px 0', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Icons.Search size={15} />
                </button>
              </form>

              {/* Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 1000, overflow: 'hidden' }}>
                  {suggestions.map((product, i) => (
                    <div key={product.id} onMouseDown={() => handleSuggestionClick(product)}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      {product.image_url?.startsWith('http')
                        ? <img src={product.image_url} alt={product.name} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                        : <div style={{ width: 32, height: 32, background: '#f0fdf4', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1rem' }}>🛒</div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.825rem', fontWeight: 600, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {product.category}
                          {product._type === 'b2b' && <span style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '0.62rem', fontWeight: 700, padding: '1px 4px', borderRadius: '3px' }}>B2B</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.825rem', fontWeight: 700, color: '#16a34a', flexShrink: 0 }}>₹{product.price}{product.unit ? `/${product.unit}` : ''}</span>
                    </div>
                  ))}
                  <div onMouseDown={() => { navigate(`/search/${encodeURIComponent(searchVal.trim())}`); setSearchVal(''); setShowSuggestions(false); }}
                    style={{ padding: '9px 12px', textAlign: 'center', fontSize: '0.78rem', fontWeight: 600, color: '#16a34a', cursor: 'pointer', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                    onMouseLeave={e => e.currentTarget.style.background = '#f9fafb'}
                  >
                    See all results for "{searchVal}"
                  </div>
                </div>
              )}
            </div>

            {/* Right actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: 'auto' }} className="navbar-actions">

              {/* User button */}
              <div style={{ position: 'relative' }}>
                <button onClick={() => user ? setShowUserMenu(!showUserMenu) : onOpenLogin()}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '30px', padding: '0 12px', borderRadius: '8px', border: '1.5px solid #e5e7eb', background: user ? '#16a34a' : '#fff', color: user ? 'white' : '#374151', cursor: 'pointer', fontSize: '0.825rem', fontWeight: 600, transition: 'all 0.15s' }} className="navbar-btn"
                  onMouseEnter={e => { if (!user) e.currentTarget.style.borderColor = '#16a34a'; }}
                  onMouseLeave={e => { if (!user) e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  <Icons.User size={16} />
                  {user ? (
                    <><span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getDisplayName()}</span><Icons.ChevronDown size={14} /></>
                  ) : (
                    <span>Sign In</span>
                  )}
                </button>

                {/* User Dropdown Menu */}
                {user && showUserMenu && (
                  <>
                    <div 
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }}
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                      minWidth: '200px',
                      zIndex: 999,
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>{getDisplayName()}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>{user.email}</div>
                      </div>
                      <div style={{ padding: '8px 0' }}>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/my-orders');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#374151',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <Icons.Orders size={18} />
                            My Orders
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/profile');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#374151',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <Icons.Profile size={18} />
                            My Profile
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/addresses');
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#374151',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                              <Icons.Address size={18} />
                              Saved Address
                            </span>
                         
                          </div>
                        </button>
                        {/* Admin link — only for admin role */}
                        {user.role === 'admin' && (
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              navigate('/admin');
                            }}
                            style={{
                              width: '100%',
                              padding: '10px 16px',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: '#16a34a',
                              fontWeight: 600,
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                              <ShieldCheckIcon style={{ width: 18, height: 18 }} />
                              Admin Dashboard
                            </span>
                          </button>
                        )}
                        <div style={{ height: '1px', background: '#e5e7eb', margin: '4px 0' }} />
                        <button
                          onClick={handleLogout}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#ef4444',
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                            <Icons.Logout size={18} />
                            Logout
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* B2C / B2B toggle */}
              <div style={{ display: 'flex', border: '1.5px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                <button onClick={() => handleUserTypeChange('b2c')}
                  style={{ height: '30px', padding: '0 14px', border: 'none', background: mode === 'b2c' ? '#16a34a' : '#fff', color: mode === 'b2c' ? 'white' : '#374151', fontWeight: 600, fontSize: '0.825rem', cursor: 'pointer', transition: 'all 0.15s' }} className="navbar-btn">
                  B2C
                </button>
                <div style={{ width: '1px', background: '#e5e7eb' }} />
                <button onClick={() => handleUserTypeChange('b2b')}
                  style={{ height: '30px', padding: '0 14px', border: 'none', background: mode === 'b2b' ? '#16a34a' : '#fff', color: mode === 'b2b' ? 'white' : '#374151', fontWeight: 600, fontSize: '0.825rem', cursor: 'pointer', transition: 'all 0.15s' }} className="navbar-btn">
                  B2B
                </button>
              </div>

              {/* Cart */}
              <button onClick={() => setIsCartOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '30px', padding: '0 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.825rem', cursor: 'pointer', boxShadow: '0 1px 4px rgba(22,163,74,0.25)', transition: 'background 0.15s', position: 'relative' }} className="navbar-btn"
                onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
                onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
              >
                <Icons.Cart size={17} />
                Cart
                {cartCount > 0 && (
                  <span style={{ background: '#fbbf24', color: '#111', borderRadius: '50%', width: '18px', height: '18px', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{cartCount}</span>
                )}
              </button>

              {/* Mobile toggle */}
              <button onClick={() => setMobileOpen(true)} className="mobile-menu-btn navbar-toggle" style={{ display: 'none', width: '36px', height: '36px', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #e5e7eb', borderRadius: '8px', background: '#fff', cursor: 'pointer', color: '#374151' }}>
                <Icons.Menu size={20} />
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* Bottom nav - Category pills — hidden on category and b2b product pages */}
      {!location.pathname.startsWith('/category/') && !location.pathname.startsWith('/b2b/products/') && (
      <nav style={{ borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
        <div className="container">
          <style>{`.km-nav-pills::-webkit-scrollbar{display:none}`}</style>
          <div className="km-nav-pills navbar-category-pills" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 0', overflowX: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            {(mode === 'b2b' ? b2bNavCategories : dataCategories).map(cat => {
              const isB2B = mode === 'b2b';
              const active = isB2B
                ? location.pathname === `/b2b/products/${encodeURIComponent(cat.name)}/${cat.id}`
                : location.pathname === `/category/${encodeURIComponent(cat.name)}/${cat.id}`;
              return (
                <button
                  key={cat.id}
                  onClick={() => isB2B
                    ? navigate(`/b2b/products/${encodeURIComponent(cat.name)}/${cat.id}`)
                    : navigate(`/category/${encodeURIComponent(cat.name)}/${cat.id}`)
                  }
                  style={{
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '5px 12px 5px 6px',
                    borderRadius: '20px',
                    border: `1.5px solid ${active ? '#16a34a' : '#e5e7eb'}`,
                    background: active ? '#f0fdf4' : '#fff',
                    color: active ? '#16a34a' : '#374151',
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                  }} className="navbar-category-pill"
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.color = '#16a34a'; e.currentTarget.style.background = '#f0fdf4'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = '#fff'; } }}
                >
                  {isB2B && cat.icon ? (
                    <span style={{ fontSize: '1rem' }}>{cat.icon}</span>
                  ) : cat.image ? (
                    <img src={cat.image} alt={cat.name} style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                  ) : null}
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.5)' }} onClick={() => setMobileOpen(false)}>
          <div style={{ position: 'fixed', top: 0, left: 0, width: 300, height: '100vh', background: '#fff', zIndex: 999, padding: '1.5rem', overflowY: 'auto', boxShadow: '4px 0 24px rgba(0,0,0,0.15)' }} className="mobile-menu" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src="/favicon.svg" alt="" width={26} height={26} style={{ display: 'block', borderRadius: 6 }} />
                <span style={{ fontWeight: 900, fontSize: '1.25rem', color: '#16a34a' }}>ePayhub Store</span>
              </div>
              <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem' }}>✕</button>
            </div>
            <form onSubmit={handleSearch} style={{ marginBottom: '1rem' }}>
              <input type="search" placeholder="Search products..." 
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </form>
            
            {!user ? (
              <>
                <button onClick={() => { onOpenLogin(); setMobileOpen(false); }} style={{ width: '100%', padding: '11px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', marginBottom: '8px' }}>Sign In</button>
                <button onClick={() => { onOpenSignup(); setMobileOpen(false); }} style={{ width: '100%', padding: '11px', background: 'transparent', color: '#16a34a', border: '2px solid #16a34a', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', marginBottom: '1.5rem' }}>Create Account</button>
              </>
            ) : (
              <div style={{ marginBottom: '1.5rem', padding: '12px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontWeight: 600, color: '#166534', marginBottom: '12px' }}>👋 Welcome, {getDisplayName()}!</div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button onClick={() => { navigate('/my-orders'); setMobileOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#374151', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
                    <Icons.Orders size={18} /> My Orders
                  </button>
                  <button onClick={() => { navigate('/profile'); setMobileOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#374151', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
                    <Icons.Profile size={18} /> My Profile
                  </button>
                  <button onClick={() => { navigate('/addresses'); setMobileOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', color: '#374151', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Icons.Address size={18} /> Saved Address
                      </span>
                      {currentLocation && (
                        <span style={{ fontSize: '0.7rem', color: '#6b7280', paddingLeft: '28px' }}>
                          {currentLocation.pincode || currentLocation.city}
                        </span>
                      )}
                    </div>
                  </button>
                </div>

                <button onClick={handleLogout} style={{ marginTop: '12px', width: '100%', padding: '8px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Logout</button>
                  {/* Admin link in mobile menu */}
                  {user.role === 'admin' && (
                    <button
                      onClick={() => { navigate('/admin'); setMobileOpen(false); }}
                      style={{ marginTop: '8px', width: '100%', padding: '8px', background: '#0f172a', color: '#4ade80', border: '1px solid #4ade80', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <ShieldCheckIcon style={{ width: 18, height: 18 }} />
                      Admin Dashboard
                    </button>
                  )}
              </div>
            )}

            {/* Mobile Delivery Location */}
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Delivery to</p>
              <button 
                onClick={() => { onOpenLocation(); setMobileOpen(false); }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: '#f9fafb',
                  border: '1.5px solid #e5e7eb',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <div style={{ color: '#16a34a' }}>
                  <Icons.Location size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 600, 
                    color: '#1f2937', 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis' 
                  }}>
                    {currentLocation ? (currentLocation.address || currentLocation.city || currentLocation.pincode) : 'Select Location'}
                  </div>
                  {currentLocation && currentLocation.pincode && (
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Pincode: {currentLocation.pincode}</div>
                  )}
                </div>
                <div style={{ color: '#9ca3af' }}>
                  <Icons.ChevronDown size={16} />
                </div>
              </button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                {mode === 'b2b' ? 'Wholesale Categories' : 'Categories'}
              </p>
              {(mode === 'b2b' ? b2bNavCategories : dataCategories).map(cat => {
                const active = mode === 'b2b'
                  ? location.pathname === `/b2b/products/${encodeURIComponent(cat.name)}/${cat.id}`
                  : location.pathname === `/category/${encodeURIComponent(cat.name)}/${cat.id}`;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      if (mode === 'b2b') {
                        navigate(`/b2b/products/${encodeURIComponent(cat.name)}/${cat.id}`);
                      } else {
                        navigate(`/category/${encodeURIComponent(cat.name)}/${cat.id}`);
                      }
                      setMobileOpen(false);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '9px 12px', borderRadius: '8px',
                      color: active ? '#16a34a' : '#374151',
                      fontSize: '0.875rem',
                      background: active ? '#f0fdf4' : '#f9fafb',
                      fontWeight: active ? 600 : 500,
                      border: 'none', textAlign: 'left', cursor: 'pointer', width: '100%'
                    }}>
                    {mode === 'b2b' && cat.icon ? (
                      <span style={{ width: 24, textAlign: 'center', flexShrink: 0 }}>{cat.icon}</span>
                    ) : cat.image ? (
                      <img src={cat.image} alt={cat.name} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
                    ) : null}
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}